import type { CharGrid } from './chargrid';

const FONT_STACK = "LibertinusMono, 'Consolas', 'Monaco', 'Liberation Mono', monospace";

/** Target character columns on a wide viewport; density adapts around this. */
const TARGET_COLS = 190;
const MIN_FONT_PX = 12;
const MAX_FONT_PX = 22;
const MAX_ATLAS_TILES = 2048;

/**
 * Draws a CharGrid to canvas, devicePixelRatio-crisp, using a lazy glyph
 * atlas (one pre-rendered tile per style+char pair) so the hot path is
 * drawImage instead of fillText.
 */
export class CanvasRenderer {
  private ctx: CanvasRenderingContext2D;
  private cellW = 8;
  private cellH = 16;
  private fontPx = 14;
  private cols = 0;
  private rows = 0;
  private dpr = 1;
  private atlas = new Map<string, HTMLCanvasElement>();

  constructor(private canvas: HTMLCanvasElement) {
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Cannot get 2d context');
    this.ctx = ctx;
  }

  /**
   * Measure the container, pick a font size for a comfortable character
   * density, and size the backing store for the current devicePixelRatio.
   * Returns the grid dimensions the rasterizer should use.
   */
  fit(): { cols: number; rows: number } {
    const host = this.canvas.parentElement ?? this.canvas;
    const rect = host.getBoundingClientRect();
    const w = Math.max(rect.width, 320);
    const h = Math.max(rect.height, 240);

    this.dpr = Math.max(window.devicePixelRatio || 1, 1);
    this.fontPx = Math.min(Math.max(Math.round(w / TARGET_COLS / 0.6), MIN_FONT_PX), MAX_FONT_PX);

    this.ctx.font = `${this.fontPx}px ${FONT_STACK}`;
    const measured = this.ctx.measureText('M').width;
    this.cellW = Math.max(Math.ceil(measured), 4);
    this.cellH = Math.round(this.fontPx * 1.15);

    this.cols = Math.max(40, Math.floor(w / this.cellW));
    this.rows = Math.max(16, Math.floor(h / this.cellH));

    this.canvas.width = Math.round(this.cols * this.cellW * this.dpr);
    this.canvas.height = Math.round(this.rows * this.cellH * this.dpr);
    this.canvas.style.width = `${this.cols * this.cellW}px`;
    this.canvas.style.height = `${this.rows * this.cellH}px`;

    this.atlas.clear();
    return { cols: this.cols, rows: this.rows };
  }

  getDimensions(): { cols: number; rows: number } {
    return { cols: this.cols, rows: this.rows };
  }

  private tile(char: string, style: string): HTMLCanvasElement {
    const key = `${style} ${char}`;
    let t = this.atlas.get(key);
    if (t) return t;
    if (this.atlas.size >= MAX_ATLAS_TILES) this.atlas.clear();

    t = document.createElement('canvas');
    t.width = Math.max(Math.round(this.cellW * this.dpr), 1);
    t.height = Math.max(Math.round(this.cellH * this.dpr), 1);
    const tc = t.getContext('2d');
    if (tc) {
      tc.scale(this.dpr, this.dpr);
      tc.font = `${this.fontPx}px ${FONT_STACK}`;
      tc.textBaseline = 'top';
      tc.fillStyle = style || '#00ff41';
      tc.fillText(char, 0, 1);
    }
    this.atlas.set(key, t);
    return t;
  }

  render(grid: CharGrid): void {
    const ctx = this.ctx;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    ctx.imageSmoothingEnabled = false;

    const cw = this.cellW * this.dpr;
    const ch = this.cellH * this.dpr;
    const width = Math.min(grid.width, this.cols);
    const height = Math.min(grid.height, this.rows);

    for (let row = 0; row < height; row++) {
      const y = Math.round(row * ch);
      const base = row * grid.width;
      for (let col = 0; col < width; col++) {
        const cell = grid.cells[base + col];
        if (cell.char === ' ') continue;
        ctx.drawImage(this.tile(cell.char, cell.style), Math.round(col * cw), y);
      }
    }
  }
}
